'use client'
import Title from '@/app/components/common/Title'
import DestinationCard from '@/app/components/DestinationCard'
import { Icons } from '@/app/components/ui/Icons'
import {
	DATE_FORMAT_QUERY,
	DEFAULT_CURRENCY,
	DEFAULT_LOCALE,
	EUROPE_DESTINATIONS,
	INIT_DATA_STORAGE_KEY,
	TABLET_DOWN,
	US_DESTINATIONS,
} from '@/app/constants'
import '@/app/styles/components/PopularDestinations.css'
import { UserData } from '@/app/types'
import { useLocalStorage, useMediaQuery } from '@uidotdev/usehooks'
import { Currencies } from 'currencies-map'
import { addDays, format } from 'date-fns'
import { EmblaOptionsType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import { useTranslations } from 'next-intl'
import dynamic from 'next/dynamic'
import { useSearchParams } from 'next/navigation'
import { FC, useCallback, useEffect, useState } from 'react'

const equalCurrencies = ['USD', 'EUR', 'GBP', 'AUD', 'CAD', 'SGD', 'CHF']

interface PopularDestinationsProps {
	lang: string
}

const PopularDestinations: FC<PopularDestinationsProps> = ({ lang }) => {
	const searchParams = useSearchParams()
	const isTabletDown = useMediaQuery(TABLET_DOWN)
	const [userData, _] = useLocalStorage<UserData>(INIT_DATA_STORAGE_KEY)
	const [isFromUS, setIsFromUS] = useState(false)
	const [currencySymbol, setCurrencySymbol] = useState('$')
	const [lastProperCurrency, setLastProperCurrency] = useState('$')
	const startDate = format(addDays(new Date(), 7), DATE_FORMAT_QUERY)
	const endDate = format(addDays(new Date(), 14), DATE_FORMAT_QUERY)

	const t = useTranslations('Main')
	const tCities = useTranslations('Cities')

	const currency =
		searchParams.get('currency') ||
		userData?.selectedCurrency?.abbreviation ||
		userData?.defaultCurrency?.abbreviation ||
		DEFAULT_CURRENCY

	useEffect(() => {
		if (equalCurrencies.includes(currency)) {
			setLastProperCurrency(
				Currencies.symbols.get(currency) || lastProperCurrency
			)
			setCurrencySymbol(Currencies.symbols.get(currency) || currencySymbol)
		} else {
			setCurrencySymbol(lastProperCurrency)
		}
	}, [userData?.defaultCurrency, userData?.selectedCurrency])

	useEffect(() => {
		if (userData && userData.userCountry === 'US') {
			setIsFromUS(true)
		}
	}, [userData])

	const options: EmblaOptionsType = {
		loop: true,
		align: 'start',
		containScroll: 'trimSnaps',
		dragFree: false,
		slidesToScroll: 1,
	}

	const [emblaRef, emblaApi] = useEmblaCarousel(options)

	const scrollPrev = useCallback(() => {
		if (emblaApi) emblaApi.scrollPrev()
	}, [emblaApi])

	const scrollNext = useCallback(() => {
		if (emblaApi) emblaApi.scrollNext()
	}, [emblaApi])

	const destinations = isFromUS ? US_DESTINATIONS : EUROPE_DESTINATIONS

	return (
		<div className="relative xl:pb-11">
			<Title
				level="3"
				size="xl"
				color="black"
				bold
				className="block text-center md:text-2.5xl xl:text-3.2xl mb-0 xl:mb-11 uppercase -tracking-[.02em]"
			>
				{t('popularDestinations')}
			</Title>

			{isTabletDown ? (
				<div className="popular-destinations-carousel">
					<div className="embla__viewport" ref={emblaRef}>
						<div className="embla__container">
							{destinations.map((dest, index) => (
								<div className="embla__slide" key={index}>
									<DestinationCard
										destination={tCities(dest.destination)}
										image={dest.image}
										price={dest.price}
										startDate={startDate}
										endDate={endDate}
										url={dest.url}
										placeId={dest.placeId}
										currencySymbol={currencySymbol}
										lang={lang || DEFAULT_LOCALE}
									/>
								</div>
							))}
						</div>
					</div>

					<div className="carousel-controls">
						<div className="carousel-arrows">
							<button
								onClick={scrollPrev}
								className="carousel-arrow carousel-arrow-prev"
								aria-label="Previous slide"
							>
								<Icons.chevron className="rotate-180" />
							</button>

							<button
								onClick={scrollNext}
								className="carousel-arrow carousel-arrow-next"
								aria-label="Next slide"
							>
								<Icons.chevron />
							</button>
						</div>
					</div>
				</div>
			) : (
				<div className="container flex gap-4">
					{destinations.map((dest, index) => (
						<DestinationCard
							key={index}
							destination={tCities(dest.destination)}
							image={dest.image}
							price={dest.price}
							startDate={startDate}
							endDate={endDate}
							url={dest.url}
							placeId={dest.placeId}
							currencySymbol={currencySymbol}
							lang={lang || DEFAULT_LOCALE}
						/>
					))}
				</div>
			)}
		</div>
	)
}

export default dynamic(() => Promise.resolve(PopularDestinations), {
	ssr: false,
})
